/**
 * Automatic discounts (F-DEAL-N3).
 *
 * MIRRORED, three ways — change them together:
 *   backend/internal/pricing/auto_discount.go   (the authority)
 *   frontend/src/lib/autoDiscounts.ts            (counter / Server tablet preview)
 *   storefront/src/lib/autoDiscounts.ts          (online ordering preview)
 * Pinned equal by backend/internal/pricing/testdata/auto_discounts/cases.json.
 *
 * BEST SINGLE DISCOUNT WINS: only the single most valuable rule applies.
 * Lines with a manual line discount are excluded.
 */
import { ONLINE_CHANNEL, roundMoney, windowMatches, type MenuClock, type TimeWindow } from './menuRules'

export type AutoDiscountKind = 'buy_x_get_y' | 'multi_buy' | 'spend_over'
export type AutoRewardType = '' | 'percent' | 'amount' | 'free_item'

export interface AutoDiscountRule extends TimeWindow {
  id: string
  name: string
  kind: AutoDiscountKind
  buy_qty: number
  get_qty: number
  get_percent: number
  bundle_qty: number
  bundle_price: number
  min_subtotal: number
  reward_type: AutoRewardType
  reward_value: number
  reward_product_id: string
  max_discount: number
  buy_product_ids: string[]
  buy_category_ids: string[]
  get_product_ids: string[]
  get_category_ids: string[]
  /** The rule was written for certain items; with all of them deleted it is inert. */
  buy_targeted?: boolean
  get_targeted?: boolean
  order_types: string[]
  channels: string[]
  valid_from: string
  valid_to: string
}

export interface DiscountLine {
  product_id: string
  category_id: string
  unit_price: number
  quantity: number
  excluded?: boolean
}

export interface DiscountContext {
  order_type: string
  channel: 'till' | 'online'
  clock: MenuClock
}

export interface AutoDiscountResult {
  rule_id: string
  name: string
  amount: number
}

export interface AutoDiscountHint {
  rule_id: string
  name: string
  kind: 'add_qty' | 'add_amount' | 'add_item'
  qty?: number
  amount?: number
  product_id?: string
}

/** Receipt / pay-panel label. Must match promo_scope.go autoDiscountReceiptLabel. */
export function autoDiscountLabel(name: string): string {
  return `Deal · ${name}`
}

function containsFold(list: readonly string[] | null | undefined, v: string): boolean {
  const needle = (v ?? '').trim().toLowerCase()
  if (needle === '' || !list) return false
  return list.some((x) => x.trim().toLowerCase() === needle)
}

function targetMatches(productIds: readonly string[], categoryIds: readonly string[], l: DiscountLine): boolean {
  if ((productIds?.length ?? 0) === 0 && (categoryIds?.length ?? 0) === 0) return true
  return containsFold(productIds, l.product_id) || containsFold(categoryIds, l.category_id)
}

function channelMatches(types: readonly string[], orderType: string, online: boolean): boolean {
  if (!types || types.length === 0) return true
  const ot = orderType.trim().toLowerCase()
  for (const raw of types) {
    const t = raw.trim().toLowerCase()
    if (t !== '' && t === ot) return true
    if (t === ONLINE_CHANNEL && online) return true
  }
  return false
}

export function ruleEligible(r: AutoDiscountRule, ctx: DiscountContext): boolean {
  if (r.channels && r.channels.length > 0 && !containsFold(r.channels, ctx.channel)) return false
  if (!channelMatches(r.order_types ?? [], ctx.order_type, ctx.channel === 'online')) return false
  if (!windowMatches(r, ctx.clock)) return false
  if (r.valid_from && ctx.clock.date < r.valid_from) return false
  if (r.valid_to && ctx.clock.date > r.valid_to) return false
  return true
}

interface Unit {
  price: number
  line: number
  seq: number
  inBuy: boolean
  inGet: boolean
}

function expandUnits(r: AutoDiscountRule, lines: readonly DiscountLine[]): Unit[] {
  const out: Unit[] = []
  const hasGet = (r.get_product_ids?.length ?? 0) > 0 || (r.get_category_ids?.length ?? 0) > 0
  lines.forEach((l, i) => {
    if (l.excluded || l.quantity < 1 || l.unit_price <= 0) return
    const inBuy = targetMatches(r.buy_product_ids ?? [], r.buy_category_ids ?? [], l)
    const inGet = hasGet ? targetMatches(r.get_product_ids ?? [], r.get_category_ids ?? [], l) : false
    if (!inBuy && !inGet) return
    for (let q = 0; q < l.quantity; q++) out.push({ price: l.unit_price, line: i, seq: q, inBuy, inGet })
  })
  return out
}

function byPrice(us: Unit[], desc: boolean): void {
  us.sort((a, b) => {
    if (a.price !== b.price) return desc ? b.price - a.price : a.price - b.price
    if (a.line !== b.line) return a.line - b.line
    return a.seq - b.seq
  })
}

function eligibleBillBase(lines: readonly DiscountLine[]): number {
  let base = 0
  for (const l of lines) {
    if (l.excluded || l.quantity < 1 || l.unit_price <= 0) continue
    base += l.unit_price * l.quantity
  }
  return base
}

function evaluateRule(r: AutoDiscountRule, lines: readonly DiscountLine[]): [number, AutoDiscountHint | null] {
  const hint = (kind: AutoDiscountHint['kind'], qty: number, amount: number, productId: string): AutoDiscountHint => {
    const h: AutoDiscountHint = { rule_id: r.id, name: r.name, kind }
    if (qty) h.qty = qty
    const a = roundMoney(amount)
    if (a) h.amount = a
    if (productId) h.product_id = productId
    return h
  }
  const singleProduct = (ids: readonly string[], cats: readonly string[]) =>
    (ids?.length ?? 0) === 1 && (cats?.length ?? 0) === 0 ? ids[0] : ''
  let amount = 0
  let h: AutoDiscountHint | null = null

  if (
    (r.buy_targeted && (r.buy_product_ids?.length ?? 0) === 0 && (r.buy_category_ids?.length ?? 0) === 0) ||
    (r.get_targeted && (r.get_product_ids?.length ?? 0) === 0 && (r.get_category_ids?.length ?? 0) === 0)
  ) {
    return [0, null]
  }

  switch (r.kind) {
    case 'buy_x_get_y': {
      if (r.buy_qty < 1 || r.get_qty < 1 || r.get_percent <= 0) return [0, null]
      const pct = Math.min(r.get_percent, 100) / 100
      const sameSet = (r.get_product_ids?.length ?? 0) === 0 && (r.get_category_ids?.length ?? 0) === 0
      const units = expandUnits(r, lines)
      if (sameSet) {
        const buys = units.filter((u) => u.inBuy)
        byPrice(buys, true)
        const g = r.buy_qty + r.get_qty
        const groups = Math.floor(buys.length / g)
        for (let k = 0; k < groups; k++) {
          for (let p = k * g + r.buy_qty; p < k * g + g; p++) amount += buys[p].price * pct
        }
        const rem = buys.length % g
        if (rem > 0) h = hint('add_qty', g - rem, 0, singleProduct(r.buy_product_ids, r.buy_category_ids))
        break
      }
      const gets = units.filter((u) => u.inGet)
      byPrice(gets, false)
      const buysOnly = units.filter((u) => u.inBuy && !u.inGet)
      const buysBoth = units.filter((u) => u.inBuy && u.inGet)
      byPrice(buysOnly, true)
      byPrice(buysBoth, true)
      const buyPool = [...buysOnly, ...buysBoth]
      const key = (u: Unit) => `${u.line}:${u.seq}`
      const used = new Set<string>()
      for (;;) {
        const picked: Unit[] = []
        for (const u of gets) {
          if (picked.length === r.get_qty) break
          if (!used.has(key(u))) picked.push(u)
        }
        if (picked.length < r.get_qty) break
        const pickedSet = new Set(picked.map(key))
        const buyers: Unit[] = []
        for (const u of buyPool) {
          if (buyers.length === r.buy_qty) break
          if (!used.has(key(u)) && !pickedSet.has(key(u))) buyers.push(u)
        }
        if (buyers.length < r.buy_qty) break
        for (const u of picked) {
          used.add(key(u))
          amount += u.price * pct
        }
        for (const u of buyers) used.add(key(u))
      }
      const freeBuys = buyPool.filter((u) => !used.has(key(u))).length
      if (freeBuys >= r.buy_qty) h = hint('add_item', r.get_qty, 0, singleProduct(r.get_product_ids, r.get_category_ids))
      else if (freeBuys > 0) h = hint('add_qty', r.buy_qty - freeBuys, 0, singleProduct(r.buy_product_ids, r.buy_category_ids))
      break
    }
    case 'multi_buy': {
      if (r.bundle_qty < 2 || r.bundle_price < 0) return [0, null]
      const units = expandUnits(r, lines).filter((u) => u.inBuy)
      byPrice(units, true)
      const groups = Math.floor(units.length / r.bundle_qty)
      for (let k = 0; k < groups; k++) {
        let sum = 0
        for (let p = k * r.bundle_qty; p < (k + 1) * r.bundle_qty; p++) sum += units[p].price
        if (sum > r.bundle_price) amount += sum - r.bundle_price
      }
      const rem = units.length % r.bundle_qty
      if (rem > 0) h = hint('add_qty', r.bundle_qty - rem, 0, singleProduct(r.buy_product_ids, r.buy_category_ids))
      break
    }
    case 'spend_over': {
      let base = 0
      for (const l of lines) {
        if (l.excluded || l.quantity < 1 || l.unit_price <= 0) continue
        if (targetMatches(r.buy_product_ids ?? [], r.buy_category_ids ?? [], l)) base += l.unit_price * l.quantity
      }
      base = roundMoney(base)
      if (base <= 0) return [0, null]
      if (base < r.min_subtotal) {
        if (base >= r.min_subtotal / 2) h = hint('add_amount', 0, r.min_subtotal - base, '')
        return [0, h]
      }
      if (r.reward_type === 'percent') amount = (base * Math.min(r.reward_value, 100)) / 100
      else if (r.reward_type === 'amount') amount = Math.min(r.reward_value, base)
      else if (r.reward_type === 'free_item') {
        let cheapest = -1
        for (const l of lines) {
          if (l.excluded || l.quantity < 1 || l.unit_price <= 0) continue
          if (l.product_id.toLowerCase() !== (r.reward_product_id ?? '').toLowerCase()) continue
          if (cheapest < 0 || l.unit_price < cheapest) cheapest = l.unit_price
        }
        if (cheapest < 0) {
          if (r.reward_product_id) h = hint('add_item', 1, 0, r.reward_product_id)
          return [0, h]
        }
        amount = cheapest
      }
      if (r.max_discount > 0 && amount > r.max_discount) amount = r.max_discount
      break
    }
    default:
      return [0, null]
  }

  amount = roundMoney(amount)
  const bill = roundMoney(eligibleBillBase(lines))
  if (amount > bill) amount = bill
  if (amount < 0) amount = 0
  return [amount, h]
}

/** The single most valuable rule (null = none) and every eligible rule's hint. */
export function evaluateAutoDiscounts(
  rules: readonly AutoDiscountRule[],
  lines: readonly DiscountLine[],
  ctx: DiscountContext,
): { best: AutoDiscountResult | null; hints: AutoDiscountHint[] } {
  const sorted = [...rules].sort((a, b) => {
    if (a.name !== b.name) return a.name < b.name ? -1 : 1
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  })
  let best: AutoDiscountResult | null = null
  const hints: AutoDiscountHint[] = []
  for (const r of sorted) {
    if (!ruleEligible(r, ctx)) continue
    const [amt, h] = evaluateRule(r, lines)
    if (h) hints.push(h)
    if (amt <= 0) continue
    if (!best || amt > best.amount || (amt === best.amount && r.id < best.rule_id)) {
      best = { rule_id: r.id, name: r.name, amount: amt }
    }
  }
  return { best, hints }
}

/** One-line guest-facing text for a hint ("Add 1 more to unlock ‘Buy 1 Get 1 Pizza’"). */
export function describeHint(h: AutoDiscountHint, productName?: (id: string) => string | undefined, money?: (v: number) => string): string {
  const fmt = money ?? ((v: number) => `Rs ${v.toLocaleString()}`)
  switch (h.kind) {
    case 'add_qty':
      return `Add ${h.qty ?? 1} more${h.product_id && productName?.(h.product_id) ? ` ${productName(h.product_id)}` : ''} to unlock “${h.name}”`
    case 'add_amount':
      return `Spend ${fmt(h.amount ?? 0)} more to unlock “${h.name}”`
    case 'add_item': {
      const n = h.product_id ? productName?.(h.product_id) : undefined
      return n ? `Add ${n} — “${h.name}” covers it` : `Add the reward item to use “${h.name}”`
    }
  }
}

/**
 * Only the hints worth showing. Just ONE discount applies to a bill, so a
 * nudge is only honest when following it would beat the deal already on it.
 * A hint that names one product is checked by adding that product and
 * re-running its rule; "spend Rs X more" is checked against what the rule
 * would give at its minimum spend; anything else is kept only while the bill
 * has no deal yet.
 */
export function usefulHints(
  rules: readonly AutoDiscountRule[],
  lines: readonly DiscountLine[],
  ctx: DiscountContext,
  hints: readonly AutoDiscountHint[],
  best: AutoDiscountResult | null,
  productLine: (productId: string) => { category_id: string; unit_price: number } | undefined,
): AutoDiscountHint[] {
  const current = best?.amount ?? 0
  return hints.filter((h) => {
    const rule = rules.find((r) => r.id === h.rule_id)
    if (!rule) return false
    if (h.product_id) {
      const p = productLine(h.product_id)
      if (p) {
        const added = [...lines, { product_id: h.product_id, category_id: p.category_id, unit_price: p.unit_price, quantity: h.qty ?? 1 }]
        const { best: after } = evaluateAutoDiscounts([rule], added, ctx)
        return (after?.amount ?? 0) > current
      }
    }
    if (h.kind === 'add_amount') {
      let potential = 0
      if (rule.reward_type === 'amount') potential = rule.reward_value
      else if (rule.reward_type === 'percent') potential = (rule.min_subtotal * Math.min(rule.reward_value, 100)) / 100
      else return current === 0
      if (rule.max_discount > 0) potential = Math.min(potential, rule.max_discount)
      return potential > current
    }
    return current === 0
  })
}
